// Centralized text-truncation helpers so every table / narrow column caps long
// values the same way. The full value is never lost — callers pair the truncated
// text with a `title` tooltip (see `shouldTruncate`) so hovering shows all of it.

// Industry-standard default character cap for a table cell. One long value
// (e.g. a very large amount or a long description) can't blow out a column.
// Columns override this via `maxChars` and opt out with `noTruncate`.
export const DEFAULT_MAX_CELL_CHARS = 10;

// Tighter cap for narrow / numeric columns (amounts, codes) where a huge value
// would otherwise widen the column and break the table layout.
export const NARROW_MAX_CELL_CHARS = 15;

/**
 * Returns `value` as a string, cut to `maxChars` with a trailing ellipsis when
 * it is longer. Non-string/number values and short values are returned as-is.
 */
export const truncateChars = (
  value: string | number | null | undefined,
  maxChars: number = DEFAULT_MAX_CELL_CHARS,
): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.length <= maxChars) return str;
  return `${str.slice(0, maxChars).trimEnd()}…`;
};

/** True when `value` would be shortened at `maxChars` (use to decide on a tooltip). */
export const shouldTruncate = (
  value: string | number | null | undefined,
  maxChars: number = DEFAULT_MAX_CELL_CHARS,
): boolean => {
  if (value === null || value === undefined) return false;
  return String(value).length > maxChars;
};
